/**
 * Assessments Page
 *
 * Displays list of all assessments with:
 * - Search by customer name
 * - Filter by recommendation
 * - Filter by date range
 * - Sort by date, customer, technician
 * - View assessment details
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

export default function Assessments() {
  const { user, contractor } = useAuth();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [filteredAssessments, setFilteredAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRecommendation, setFilterRecommendation] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');

  useEffect(() => {
    if (user?.contractor_id) {
      fetchAssessments();
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [assessments, searchTerm, filterRecommendation, sortBy]);

  const fetchAssessments = async () => {
    try {
      setLoading(true);

      // Fetch assessments with customer and technician info
      const { data, error: fetchError } = await supabase
        .from('assessments')
        .select(`
          *,
          customers (
            id,
            full_name,
            email,
            phone
          ),
          users!assessments_technician_id_fkey (
            id,
            full_name,
            email
          )
        `)
        .eq('contractor_id', user.contractor_id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setAssessments(data || []);
    } catch (err) {
      console.error('Error fetching assessments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...assessments];

    // Search by customer name
    if (searchTerm) {
      filtered = filtered.filter(assessment =>
        assessment.customers?.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by recommendation
    if (filterRecommendation !== 'all') {
      filtered = filtered.filter(assessment =>
        assessment.recommendation?.toLowerCase() === filterRecommendation.toLowerCase()
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'date-asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'customer':
          return (a.customers?.full_name || '').localeCompare(b.customers?.full_name || '');
        case 'score-desc':
          return (b.assessment_score || 0) - (a.assessment_score || 0);
        case 'score-asc':
          return (a.assessment_score || 0) - (b.assessment_score || 0);
        default:
          return 0;
      }
    });

    setFilteredAssessments(filtered);
  };

  const getRecommendationColor = (recommendation) => {
    switch (recommendation?.toLowerCase()) {
      case 'replace':
        return { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' };
      case 'consider':
        return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' };
      case 'repair':
        return { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' };
      case 'maintain':
        return { bg: '#dcfce7', border: '#22c55e', text: '#14532d' };
      default:
        return { bg: '#f3f4f6', border: '#9ca3af', text: '#374151' };
    }
  };

  const getRecommendationIcon = (recommendation) => {
    switch (recommendation?.toLowerCase()) {
      case 'replace':
        return '🔴';
      case 'consider':
        return '🟡';
      case 'repair':
        return '🔵';
      case 'maintain':
        return '🟢';
      default:
        return '⚪';
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading assessments...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '40px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ fontSize: '28px', marginBottom: '5px' }}>Assessment History</h1>
          <p style={{ color: '#7f8c8d', margin: 0 }}>
            View and manage all HVAC assessments ({filteredAssessments.length} of {assessments.length})
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            padding: '15px',
            background: '#fee2e2',
            border: '2px solid #ef4444',
            borderRadius: '8px',
            color: '#dc2626',
            marginBottom: '20px'
          }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Filters */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            {/* Search */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Search Customer
              </label>
              <input
                type="text"
                className="input"
                placeholder="Search by customer name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Filter by Recommendation */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Recommendation
              </label>
              <select
                className="input"
                value={filterRecommendation}
                onChange={(e) => setFilterRecommendation(e.target.value)}
              >
                <option value="all">All Recommendations</option>
                <option value="replace">Replace</option>
                <option value="consider">Consider Replacement</option>
                <option value="repair">Repair</option>
                <option value="maintain">Maintain</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Sort By
              </label>
              <select
                className="input"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date-desc">Newest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="customer">Customer Name</option>
                <option value="score-desc">Score (High to Low)</option>
                <option value="score-asc">Score (Low to High)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Assessments List */}
        {filteredAssessments.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>📋</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', marginBottom: '10px' }}>
              {searchTerm || filterRecommendation !== 'all' ? 'No matching assessments' : 'No assessments yet'}
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              {searchTerm || filterRecommendation !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first assessment to get started'}
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '15px' }}>
            {filteredAssessments.map((assessment) => {
              const colors = getRecommendationColor(assessment.recommendation);
              const icon = getRecommendationIcon(assessment.recommendation);

              return (
                <div
                  key={assessment.id}
                  onClick={() => navigate(`/assessments/${assessment.id}`)}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: '2px solid transparent'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.15)';
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div className="mobile-stack" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '20px', alignItems: 'start' }}>
                    {/* Main Info */}
                    <div>
                      {/* Customer Name */}
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#2c3e50', marginBottom: '8px' }}>
                        {assessment.customers?.full_name || 'Unknown Customer'}
                      </div>

                      {/* Details Row */}
                      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '14px', color: '#64748b', marginBottom: '12px' }}>
                        <div>
                          📅 {new Date(assessment.created_at).toLocaleDateString()}
                        </div>
                        <div>
                          👤 {assessment.users?.full_name || 'Unknown Tech'}
                        </div>
                        {assessment.system_age && (
                          <div>
                            🏠 {assessment.system_age} years old
                          </div>
                        )}
                        {assessment.assessment_score && (
                          <div>
                            📊 Score: {assessment.assessment_score}/100
                          </div>
                        )}
                      </div>

                      {/* Contact Info */}
                      {(assessment.customers?.email || assessment.customers?.phone) && (
                        <div style={{ fontSize: '13px', color: '#94a3b8' }}>
                          {assessment.customers?.email && (
                            <span style={{ marginRight: '15px' }}>✉️ {assessment.customers.email}</span>
                          )}
                          {assessment.customers?.phone && (
                            <span>📞 {assessment.customers.phone}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Recommendation Badge */}
                    <div style={{
                      padding: '12px 20px',
                      background: colors.bg,
                      border: `2px solid ${colors.border}`,
                      borderRadius: '8px',
                      textAlign: 'center',
                      minWidth: '140px'
                    }}>
                      <div style={{ fontSize: '24px', marginBottom: '4px' }}>
                        {icon}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '700',
                        color: colors.text,
                        textTransform: 'uppercase'
                      }}>
                        {assessment.recommendation || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
